import React, { useState, useEffect, useCallback } from 'react';
import {
  Box, Button, Card, CardContent, Dialog, DialogTitle, DialogContent,
  Typography, TextField, IconButton, CircularProgress, Snackbar, Grid,
  Chip, Divider, Link, LinearProgress, Tooltip, Paper,
} from '@material-ui/core';
import MUIDataTable from 'mui-datatables';
import {
  Cloud, Info, Refresh, CheckCircle, Android, PhoneIphone,
} from '@material-ui/icons';
import { Checkbox, FormControlLabel } from '@material-ui/core';

const API_URL = "https://aitool-46a4c0ff8a02.herokuapp.com";

// ---------------------------------------------------------------------------
// Shared UI atoms (mirrors AppAnalyser 2.0 style)
// ---------------------------------------------------------------------------
const STATUS_COLORS = {
  'Approved':     { bg: '#27ae60', text: '#fff' },
  'Not Approved': { bg: '#e74c3c', text: '#fff' },
  'Manual check': { bg: '#f39c12', text: '#fff' },
  'Pending':      { bg: '#636e72', text: '#fff' },
};
const StatusChip = ({ value }) => {
  const c = STATUS_COLORS[value] || { bg: '#bdc3c7', text: '#fff' };
  return <Chip label={value || '—'} size="small" style={{ backgroundColor: c.bg, color: c.text, fontWeight: 700, fontSize: 11 }} />;
};
const ScoreChip = ({ value }) => {
  if (value === 'N/A' || value === undefined || value === '') return <Chip label="N/A" size="small" style={{ background: '#bdc3c7', color: '#fff', fontWeight: 700 }} />;
  const bg = value >= 55 ? '#27ae60' : value >= 40 ? '#f39c12' : '#e74c3c';
  return <Chip label={value} size="small" style={{ backgroundColor: bg, color: '#fff', fontWeight: 700, minWidth: 40 }} />;
};
const PlatformChip = ({ value }) => {
  const isIOS = (value || '').toLowerCase() === 'ios';
  return (
    <Chip
      icon={isIOS ? <PhoneIphone style={{ fontSize: 14 }} /> : <Android style={{ fontSize: 14 }} />}
      label={isIOS ? 'iOS' : 'Android'}
      size="small"
      style={{ backgroundColor: isIOS ? '#1c1c1e' : '#3ddc84', color: isIOS ? '#fff' : '#000', fontWeight: 600, fontSize: 11 }}
    />
  );
};

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------
function formatLocalTime(dateString) {
  if (!dateString) return '—';
  const d = new Date(dateString.replace(' ', 'T') + 'Z');
  return d.toLocaleString('pl-PL', { timeZone: 'Europe/Warsaw' }).replace(',', '');
}
function getRootDomain(url) {
  if (!url) return '';
  try { const u = new URL(url); return u.protocol + '//' + u.hostname; } catch { return ''; }
}
function formatRating(r) {
  if (!r || r === '-') return '—';
  return Number(r).toFixed(2);
}

// ---------------------------------------------------------------------------
// BatchForm
// ---------------------------------------------------------------------------
function BatchForm({ onCreated }) {
  const [batchName, setBatchName] = useState('');
  const [appInput, setAppInput]   = useState('');
  const [loading, setLoading]     = useState(false);
  const [ignoreAuto, setIgnoreAuto] = useState(false);

  const appCount = appInput.split('\n').map(l => l.trim()).filter(Boolean).length;

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!batchName.trim() || !appInput.trim()) return;
    const appList = appInput.split('\n').map(line => line.trim()).filter(Boolean)
      .map(bundle => ({ bundle, platform: /^\d+$/.test(bundle) ? 'ios' : 'android' }));
    if (!appList.length) return;
    setLoading(true);
    try {
      await fetch(`${API_URL}/analyzeBatch_bg`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ batch_name: batchName, apps: appList, ignoreBlockedAndAutoapproved: ignoreAuto }),
      });
      setLoading(false);
      setBatchName('');
      setAppInput('');
      onCreated && onCreated();
    } catch {
      setLoading(false);
      alert('Failed to create batch.');
    }
  };

  return (
    <Card style={{ marginBottom: 20, borderRadius: 12, borderTop: '3px solid #fe5000', boxShadow: '0 2px 12px rgba(0,0,0,0.07)' }}>
      <CardContent>
        <Box display="flex" alignItems="center" mb={2} style={{ gap: 10 }}>
          <Cloud style={{ color: '#fe5000' }} />
          <Typography variant="h6" style={{ fontWeight: 700 }}>Create New Batch</Typography>
        </Box>
        <form onSubmit={handleSubmit}>
          <Box display="flex" style={{ gap: 12, marginBottom: 14, flexWrap: 'wrap' }}>
            <TextField
              label="Batch Name"
              value={batchName}
              onChange={e => setBatchName(e.target.value)}
              required
              variant="outlined"
              size="small"
              style={{ width: 260 }}
              placeholder="e.g. Q1 2025 Android Review"
            />
          </Box>
          <TextField
            label={`App IDs / Bundles — one per line${appCount > 0 ? ` (${appCount} app${appCount > 1 ? 's' : ''} detected)` : ''}`}
            value={appInput}
            onChange={e => setAppInput(e.target.value)}
            required
            multiline
            minRows={5}
            variant="outlined"
            placeholder={`com.example.app\n1234567890\ncom.foo.bar`}
            fullWidth
            style={{ marginBottom: 12 }}
            inputProps={{ style: { fontFamily: 'monospace', fontSize: 13 } }}
          />
          <Box display="flex" alignItems="center" style={{ gap: 16 }}>
            <FormControlLabel
              control={<Checkbox checked={ignoreAuto} onChange={e => setIgnoreAuto(e.target.checked)} color="primary" size="small" />}
              label={<Typography variant="body2">Ignore autoapproved &amp; blocked apps</Typography>}
              style={{ margin: 0 }}
            />
            <Button
              variant="contained"
              color="primary"
              type="submit"
              disabled={loading || !batchName.trim() || !appCount}
              startIcon={loading ? <CircularProgress size={18} color="inherit" /> : <Cloud />}
              style={{ borderRadius: 8, fontWeight: 700, padding: '8px 24px', marginLeft: 'auto' }}
            >
              {loading ? 'Submitting…' : `Start Batch${appCount > 0 ? ` (${appCount})` : ''}`}
            </Button>
          </Box>
        </form>
      </CardContent>
    </Card>
  );
}

// ---------------------------------------------------------------------------
// BatchList
// ---------------------------------------------------------------------------
function BatchList({ onSelect, selectedId }) {
  const [batches, setBatches] = useState([]);
  const [loading, setLoading] = useState(false);

  const fetchBatches = useCallback(() => {
    setLoading(true);
    fetch(`${API_URL}/batches`)
      .then(r => r.json())
      .then(data => { setBatches(data || []); setLoading(false); })
      .catch(() => { setBatches([]); setLoading(false); });
  }, []);

  useEffect(() => { fetchBatches(); }, [fetchBatches]);

  const sorted = [...batches].sort((a, b) => (b.created_at || '').localeCompare(a.created_at || ''));

  return (
    <Card style={{ borderRadius: 12, boxShadow: '0 2px 12px rgba(0,0,0,0.07)', height: '100%', display: 'flex', flexDirection: 'column' }}>
      <CardContent style={{ paddingBottom: 8 }}>
        <Box display="flex" alignItems="center" mb={1}>
          <Typography variant="h6" style={{ fontWeight: 700, flexGrow: 1 }}>
            Batch History
            {batches.length > 0 && (
              <Typography component="span" variant="caption" style={{ marginLeft: 8, color: '#aaa' }}>
                ({batches.length})
              </Typography>
            )}
          </Typography>
          <Tooltip title="Refresh list">
            <IconButton size="small" onClick={fetchBatches} disabled={loading}>
              {loading ? <CircularProgress size={18} /> : <Refresh />}
            </IconButton>
          </Tooltip>
        </Box>
        {loading && <LinearProgress style={{ marginBottom: 8, borderRadius: 4 }} />}
      </CardContent>

      {/* Simple scrollable batch list — no MUIDataTable issues */}
      <Box style={{ overflowY: 'auto', flexGrow: 1, padding: '0 16px 16px' }}>
        {sorted.length === 0 && !loading && (
          <Typography variant="body2" color="textSecondary" style={{ textAlign: 'center', padding: 24 }}>
            No batches yet
          </Typography>
        )}
        {sorted.map(batch => {
          const isActive = batch.id === selectedId;
          return (
            <Box
              key={batch.id}
              onClick={() => onSelect(batch.id)}
              style={{
                padding: '10px 14px',
                marginBottom: 6,
                borderRadius: 8,
                cursor: 'pointer',
                border: `1.5px solid ${isActive ? '#fe5000' : '#e8e8e8'}`,
                background: isActive ? '#fff5f0' : '#fff',
                boxShadow: isActive ? '0 2px 8px rgba(254,80,0,0.12)' : 'none',
                transition: 'all 0.15s ease',
              }}
            >
              <Box display="flex" justifyContent="space-between" alignItems="flex-start">
                <Box style={{ overflow: 'hidden', flexGrow: 1, marginRight: 8 }}>
                  <Typography
                    variant="body2"
                    style={{ fontWeight: 700, color: isActive ? '#fe5000' : '#2d3436', lineHeight: 1.3 }}
                    noWrap
                  >
                    {batch.name || `Batch #${batch.id}`}
                  </Typography>
                  <Typography variant="caption" style={{ color: '#636e72' }}>
                    {formatLocalTime(batch.created_at)}
                  </Typography>
                </Box>
                <Chip
                  label={batch.app_count != null ? `${batch.app_count} apps` : '—'}
                  size="small"
                  style={{
                    background: isActive ? '#fe500022' : '#f0f0f0',
                    color: isActive ? '#fe5000' : '#636e72',
                    fontWeight: 700,
                    fontSize: 11,
                    flexShrink: 0,
                  }}
                />
              </Box>
              {isActive && (
                <Typography variant="caption" style={{ color: '#fe5000', fontWeight: 600 }}>
                  ✓ Currently viewing
                </Typography>
              )}
            </Box>
          );
        })}
      </Box>
    </Card>
  );
}

// ---------------------------------------------------------------------------
// useBatchProgress — polls once on batchId change
// ---------------------------------------------------------------------------
function useBatchProgress(batchId) {
  const [progress, setProgress] = useState({ total: 0, done: 0 });
  const [refreshing, setRefreshing] = useState(false);

  const refresh = useCallback(() => {
    if (!batchId) return;
    setRefreshing(true);
    fetch(`${API_URL}/batch_progress/${batchId}`)
      .then(r => r.json())
      .then(p => { setProgress(p); setRefreshing(false); })
      .catch(() => setRefreshing(false));
  }, [batchId]);

  useEffect(() => { setProgress({ total: 0, done: 0 }); refresh(); }, [batchId, refresh]);
  return { progress, refresh, refreshing };
}

// ---------------------------------------------------------------------------
// BatchAppsTable
// ---------------------------------------------------------------------------
function BatchAppsTable({ batchId }) {
  const [apps, setApps]       = useState([]);
  const [loading, setLoading] = useState(false);
  const [selected, setSelected] = useState(null);
  const { progress, refresh, refreshing } = useBatchProgress(batchId);

  useEffect(() => {
    if (!batchId) return;
    setLoading(true);
    fetch(`${API_URL}/batch/${batchId}`)
      .then(r => r.json())
      .then(data => { setApps(data.apps || []); setLoading(false); })
      .catch(() => setLoading(false));
  }, [batchId]);

  const pct = progress.total > 0 ? Math.round((progress.done / progress.total) * 100) : 0;
  const isDone = progress.total > 0 && progress.done >= progress.total;

  const columns = [
    { name: 'app_id', label: 'App ID', options: { filter: false } },
    { name: 'name',   label: 'Name' },
    {
      name: 'platform', label: 'Platform',
      options: { customBodyRender: v => <PlatformChip value={v} /> }
    },
    {
      name: 'appStoreUrl', label: 'Store',
      options: { filter: false, sort: false, customBodyRender: v => v
        ? <Link href={v} target="_blank" rel="noopener noreferrer" style={{ fontWeight: 600, color: '#fe5000' }}>Open ↗</Link>
        : '—'
      }
    },
    {
      name: 'averageRating', label: '⭐ Rating',
      options: { customBodyRender: v => v ? `${Number(v).toFixed(2)}` : '—' }
    },
    { name: 'reviewCount', label: 'Reviews' },
    { name: 'installs',    label: 'Installs' },
    {
      name: 'blocked', label: 'Blocked',
      options: { customBodyRender: v => v === 'Yes'
        ? <Chip label="Blocked" size="small" style={{ background: '#e74c3c', color: '#fff', fontWeight: 700 }} />
        : <Chip label="No" size="small" style={{ background: '#ecf0f1', color: '#666' }} />
      }
    },
    {
      name: 'isMonetized', label: 'Monetized',
      options: { customBodyRender: v => v === 'Yes'
        ? <Chip label="Yes" size="small" style={{ background: '#27ae60', color: '#fff', fontWeight: 700 }} />
        : <Chip label="No" size="small" style={{ background: '#ecf0f1', color: '#666' }} />
      }
    },
    { name: 'monetizedBy', label: 'Networks' },
    { name: 'ratingDE', label: 'DE' }, { name: 'ratingES', label: 'ES' },
    { name: 'ratingFR', label: 'FR' }, { name: 'ratingIT', label: 'IT' }, { name: 'ratingPL', label: 'PL' },
    {
      name: 'approvalStatus', label: 'Status',
      options: { customBodyRender: v => <StatusChip value={v} /> }
    },
    { name: 'reason', label: 'Reason' },
    {
      name: 'score', label: 'Score',
      options: { customBodyRender: v => <ScoreChip value={v} /> }
    },
    {
      name: 'details', label: '',
      options: {
        filter: false, sort: false,
        customBodyRender: (_, tableMeta) => (
          <Tooltip title="View details">
            <IconButton size="small" onClick={() => setSelected(apps[tableMeta.rowIndex])}>
              <Info fontSize="small" />
            </IconButton>
          </Tooltip>
        )
      }
    },
  ];

  const data = (apps || []).map(app => {
    let res = {};
    try { res = app.result_json ? (typeof app.result_json === 'string' ? JSON.parse(app.result_json) : app.result_json) : {}; } catch {}
    const platform = app.platform;
    const devUrl = platform === 'ios' ? (res.sellerUrl || '') : (res.developerWebsite || '');
    const rootDomain = getRootDomain(devUrl);
    return {
      app_id: app.app_id, name: res.name || '—',
      appStoreUrl: res.shopurl || res.appStoreUrl || '',
      platform,
      averageRating: res.rating || res.averageRating || '',
      reviewCount: res.reviewCount?.toLocaleString() || '—',
      installs: platform === 'android' ? (res.fullData?.installs || res.installs || '—') : '—',
      developerPage: rootDomain,
      blocked: res.isBlocked ? 'Yes' : 'No',
      isMonetized: res.isMonetized ? 'Yes' : 'No',
      monetizedBy: res.monetizedBy ?? '—',
      ratingDE: res.ratingDE ?? '—', ratingES: res.ratingES ?? '—',
      ratingFR: res.ratingFR ?? '—', ratingIT: res.ratingIT ?? '—', ratingPL: res.ratingPL ?? '—',
      approvalStatus: res.approvalStatus ?? (app.status === 'pending' ? 'Pending' : '—'),
      reason: res.reason ?? '', score: res.score ?? '',
      details: '', result_full: res, result_row: app,
    };
  });

  // Summary stats
  const approved  = data.filter(r => r.approvalStatus === 'Approved').length;
  const manual    = data.filter(r => r.approvalStatus === 'Manual check').length;
  const rejected  = data.filter(r => r.approvalStatus === 'Not Approved').length;
  const pending   = data.filter(r => r.approvalStatus === 'Pending' || !r.approvalStatus || r.approvalStatus === '—').length;

  return (
    <Card style={{ marginTop: 8, borderRadius: 12, boxShadow: '0 2px 12px rgba(0,0,0,0.07)' }}>
      <CardContent>
        {/* Header */}
        <Box display="flex" alignItems="center" mb={2}>
          <Typography variant="h6" style={{ fontWeight: 700, flexGrow: 1 }}>Batch Results</Typography>
          <Tooltip title="Refresh progress">
            <IconButton size="small" onClick={refresh} disabled={refreshing}>
              {refreshing ? <CircularProgress size={18} /> : <Refresh />}
            </IconButton>
          </Tooltip>
        </Box>

        {/* Progress bar */}
        {progress.total > 0 && (
          <Box mb={2}>
            <Box display="flex" justifyContent="space-between" mb={0.5}>
              <Typography variant="body2" style={{ fontWeight: 600 }}>
                {isDone
                  ? <><CheckCircle style={{ fontSize: 16, color: '#27ae60', verticalAlign: 'middle', marginRight: 4 }} />Analysis complete</>
                  : `Processing… ${progress.done} / ${progress.total}`
                }
              </Typography>
              <Typography variant="body2" style={{ fontWeight: 700, color: isDone ? '#27ae60' : '#fe5000' }}>
                {pct}%
              </Typography>
            </Box>
            <LinearProgress
              variant="determinate"
              value={pct}
              style={{ borderRadius: 6, height: 8, background: '#f0f0f0' }}
              color={isDone ? 'primary' : 'secondary'}
            />
          </Box>
        )}

        {/* Stats row (only when results are in) */}
        {data.length > 0 && (
          <Box display="flex" flexWrap="wrap" style={{ gap: 8, marginBottom: 16 }}>
            {[
              { label: 'Total',        value: data.length, color: '#636e72' },
              { label: 'Approved',     value: approved,    color: '#27ae60' },
              { label: 'Manual Check', value: manual,      color: '#f39c12' },
              { label: 'Not Approved', value: rejected,    color: '#e74c3c' },
              { label: 'Pending',      value: pending,     color: '#b2bec3' },
            ].map(s => (
              <Paper key={s.label} elevation={0} style={{
                padding: '8px 14px', borderRadius: 8, textAlign: 'center',
                background: `${s.color}18`, border: `1px solid ${s.color}44`,
              }}>
                <Typography style={{ fontSize: 20, fontWeight: 800, color: s.color, lineHeight: 1 }}>{s.value}</Typography>
                <Typography variant="caption" style={{ color: '#666', fontWeight: 600 }}>{s.label}</Typography>
              </Paper>
            ))}
          </Box>
        )}

        {loading && <LinearProgress style={{ marginBottom: 12, borderRadius: 4 }} />}

        <MUIDataTable
          title={data.length > 0 ? `${data.length} apps` : false}
          data={data}
          columns={columns}
          options={{
            filter: true,
            selectableRows: 'none',
            responsive: 'standard',
            rowsPerPage: 25,
            elevation: 0,
            onRowClick: (_, rowMeta) => setSelected(apps[rowMeta.dataIndex]),
            downloadOptions: { filename: `batch_${batchId}_${new Date().toISOString().slice(0, 10)}.csv` },
            textLabels: { body: { noMatch: loading ? 'Loading…' : 'No apps in this batch' } },
          }}
        />

        <AppDetailsModal open={!!selected} app={selected} onClose={() => setSelected(null)} />
      </CardContent>
    </Card>
  );
}

// ---------------------------------------------------------------------------
// AppDetailsModal
// ---------------------------------------------------------------------------
function AppDetailsModal({ open, app, onClose }) {
  const [zoomImg, setZoomImg]           = useState(null);
  const [reviews, setReviews]           = useState([]);
  const [brandSafe, setBrandSafe]       = useState(undefined);
  const [reviewsLoading, setReviewsLoading] = useState(false);
  const [reviewsError, setReviewsError] = useState('');
  const [showReviews, setShowReviews]   = useState(false);

  useEffect(() => {
    if (open) { setShowReviews(false); setReviews([]); setBrandSafe(undefined); setReviewsError(''); }
  }, [open, app]);

  const handleFetchReviews = async () => {
    if (!app) return;
    setReviews([]); setBrandSafe(undefined); setReviewsLoading(true); setReviewsError(''); setShowReviews(true);
    let res = {};
    try { res = app.result_json ? (typeof app.result_json === 'string' ? JSON.parse(app.result_json) : app.result_json) : {}; } catch {}
    const bundle = app.app_id || res.id || res.bundleId;
    let platform = app.platform || res.platform || (/^\d+$/.test(bundle) ? 'apple' : 'google');
    if (platform === 'ios') platform = 'apple';
    if (platform === 'android') platform = 'google';
    try {
      const resp = await fetch(`${API_URL}/analyzeApp`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ bundle, platform }),
      });
      if (!resp.ok) throw new Error();
      const d = await resp.json();
      setReviews(d.reviews || []); setBrandSafe(d.brand_safe ?? undefined);
    } catch { setReviewsError('Error loading reviews'); }
    finally { setReviewsLoading(false); }
  };

  if (!app) return null;
  let res = {};
  try { res = app.result_json ? (typeof app.result_json === 'string' ? JSON.parse(app.result_json) : app.result_json) : {}; } catch {}

  const platform     = app.platform || res.platform;
  const app_id       = app.app_id || res.id || res.bundleId;
  const name         = res.name || app.name || '';
  const primaryGenre = res.primaryGenre || res.genre || '';
  const iconUrl      = res.iconUrl || res.imageUrl || res.fullData?.artworkUrl100 || '';
  const devUrl       = platform === 'ios' ? (res.sellerUrl || '') : (res.developerWebsite || '');
  const rootDomain   = getRootDomain(devUrl);
  const appAdsTxtUrl = rootDomain ? rootDomain + '/app-ads.txt' : '';
  const approvalStatus = res.approvalStatus ?? '—';
  const score          = res.score ?? '—';
  const reason         = res.reason ?? '';
  const statusColor    = STATUS_COLORS[approvalStatus] || { bg: '#bdc3c7', text: '#fff' };

  let screenshots = (
    res.fullData?.screenshotUrls ||
    res.fullData?.ipadScreenshotUrls ||
    res.fullData?.screenshots ||
    res.screenshotUrls ||
    res.screenshots || []
  );
  screenshots = Array.isArray(screenshots) ? screenshots.filter(Boolean) : [];

  return (
    <>
      <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth PaperProps={{ style: { borderRadius: 14 } }}>
        <DialogTitle style={{ borderBottom: '1px solid #f0f0f0', paddingBottom: 12 }}>
          <Box display="flex" alignItems="center" style={{ gap: 14 }}>
            {iconUrl && (
              <img src={iconUrl} alt="icon"
                style={{ width: 52, height: 52, borderRadius: 12, objectFit: 'cover', flexShrink: 0 }} />
            )}
            <Box style={{ flexGrow: 1, overflow: 'hidden' }}>
              <Typography variant="h6" style={{ fontWeight: 700 }} noWrap>{name || app_id}</Typography>
              <Box display="flex" alignItems="center" style={{ gap: 6, marginTop: 4 }}>
                <PlatformChip value={platform} />
                {primaryGenre && <Typography variant="caption" color="textSecondary">{primaryGenre}</Typography>}
              </Box>
            </Box>
            {(res.appStoreUrl || res.shopurl) && (
              <Link href={res.appStoreUrl || res.shopurl} target="_blank" rel="noopener" underline="none">
                <Chip label="Store ↗" size="small" style={{ background: '#fe5000', color: '#fff', fontWeight: 700, cursor: 'pointer' }} />
              </Link>
            )}
          </Box>
        </DialogTitle>

        <DialogContent style={{ paddingTop: 20 }}>
          <Grid container spacing={3}>
            {/* Left — details */}
            <Grid item xs={12} md={7}>

              {/* Status & Score */}
              <Box display="flex" alignItems="center" style={{ gap: 8, marginBottom: 16 }}>
                <StatusChip value={approvalStatus} />
                <ScoreChip value={score} />
                {reason && <Typography variant="caption" style={{ color: statusColor.bg, fontWeight: 600 }}>{reason}</Typography>}
              </Box>

              {/* Monetization */}
              <Box display="flex" style={{ gap: 6, marginBottom: 16, flexWrap: 'wrap' }}>
                <Chip
                  label={res.isBlocked ? 'Blocked' : 'Not Blocked'} size="small"
                  style={{ background: res.isBlocked ? '#e74c3c' : '#ecf0f1', color: res.isBlocked ? '#fff' : '#666', fontWeight: 700 }}
                />
                <Chip
                  label={res.isMonetized ? 'Monetized' : 'Not Monetized'} size="small"
                  style={{ background: res.isMonetized ? '#27ae60' : '#ecf0f1', color: res.isMonetized ? '#fff' : '#666', fontWeight: 700 }}
                />
                {res.monetizedBy > 0 && (
                  <Chip label={`${res.monetizedBy} networks`} size="small" style={{ background: '#dfe6e9', color: '#333' }} />
                )}
              </Box>

              <Divider style={{ marginBottom: 14 }} />

              {/* Rating row */}
              <Box mb={1.5}>
                <Typography variant="body2" color="textSecondary" style={{ fontWeight: 600, marginBottom: 4 }}>Rating &amp; Reviews</Typography>
                <Box display="flex" alignItems="center" style={{ gap: 10 }}>
                  <Typography style={{ fontWeight: 800, fontSize: 22, color: '#2d3436' }}>
                    {formatRating(res.averageRating ?? res.rating)} ⭐
                  </Typography>
                  <Typography variant="caption" color="textSecondary">
                    {(res.reviewCount || res.reviews || 0).toLocaleString()} reviews
                  </Typography>
                </Box>
                {/* Country ratings */}
                <Box display="flex" style={{ gap: 8, marginTop: 6, flexWrap: 'wrap' }}>
                  {[['DE', res.ratingDE], ['ES', res.ratingES], ['FR', res.ratingFR], ['IT', res.ratingIT], ['PL', res.ratingPL]].map(([cc, val]) => val ? (
                    <Chip key={cc} label={`${cc}: ${formatRating(val)}`} size="small"
                      style={{ background: '#f0f0f0', fontSize: 11 }} />
                  ) : null)}
                </Box>
                <Box mt={1} display="flex" alignItems="center" style={{ gap: 10 }}>
                  <Button variant="outlined" size="small" onClick={handleFetchReviews} disabled={reviewsLoading}
                    style={{ borderRadius: 6 }}>
                    {reviewsLoading ? <CircularProgress size={16} /> : 'Show Reviews'}
                  </Button>
                  {brandSafe !== undefined && (
                    <Chip
                      label={`Brand Safe: ${brandSafe ? 'Yes' : 'No'}`} size="small"
                      style={{ background: brandSafe ? '#27ae60' : '#e74c3c', color: '#fff', fontWeight: 700 }}
                    />
                  )}
                </Box>
                {showReviews && (
                  <Box mt={1}>
                    {reviewsError && <Typography color="error" variant="caption">{reviewsError}</Typography>}
                    {!reviewsLoading && reviews.length > 0 && (
                      <Box style={{ maxHeight: 200, overflowY: 'auto', border: '1px solid #eee', borderRadius: 8, padding: 10 }}>
                        {reviews.map((r, i) => (
                          <Typography key={i} variant="body2" style={{ marginBottom: 8, color: '#444', whiteSpace: 'pre-line' }}>{r}</Typography>
                        ))}
                      </Box>
                    )}
                    {!reviewsLoading && reviews.length === 0 && !reviewsError && (
                      <Typography variant="caption" color="textSecondary">No reviews found.</Typography>
                    )}
                  </Box>
                )}
              </Box>

              <Divider style={{ marginBottom: 14 }} />

              {/* Developer / IDs */}
              {[
                ['Developer', res.artistName || res.sellerName || res.developerName],
                ['App ID',    app_id],
                ['App-ads.txt', appAdsTxtUrl ? appAdsTxtUrl : null],
              ].filter(([, v]) => v).map(([label, value]) => (
                <Box key={label} display="flex" justifyContent="space-between" mb={0.75} alignItems="flex-start">
                  <Typography variant="caption" color="textSecondary" style={{ fontWeight: 600, minWidth: 90 }}>{label}</Typography>
                  {label === 'App-ads.txt'
                    ? <Link href={value} target="_blank" rel="noopener" variant="caption" style={{ color: '#fe5000' }}>{value}</Link>
                    : <Typography variant="caption" style={{ fontWeight: 600, textAlign: 'right', maxWidth: '65%' }}>{value}</Typography>
                  }
                </Box>
              ))}
              {rootDomain && (
                <Box display="flex" justifyContent="space-between" mb={0.75}>
                  <Typography variant="caption" color="textSecondary" style={{ fontWeight: 600, minWidth: 90 }}>Developer Page</Typography>
                  <Link href={rootDomain} target="_blank" rel="noopener" variant="caption" style={{ color: '#fe5000' }}>
                    {rootDomain.replace(/^https?:\/\//, '')}
                  </Link>
                </Box>
              )}

              {/* Description */}
              {(res.fullData?.description || res.description) && (
                <Box mt={1.5}>
                  <Typography variant="caption" color="textSecondary" style={{ fontWeight: 600 }}>Description</Typography>
                  <Typography variant="body2" style={{ marginTop: 4, color: '#555', whiteSpace: 'pre-line', fontSize: 13 }}>
                    {(res.fullData?.description || res.description || '').slice(0, 400)}
                    {(res.fullData?.description || res.description || '').length > 400 ? '…' : ''}
                  </Typography>
                </Box>
              )}
            </Grid>

            {/* Right — screenshots */}
            <Grid item xs={12} md={5}>
              <Typography variant="caption" color="textSecondary" style={{ fontWeight: 600 }}>
                Screenshots {screenshots.length > 0 ? `(${screenshots.length})` : ''}
              </Typography>
              {screenshots.length > 0 ? (
                <Box style={{ display: 'flex', flexWrap: 'wrap', gap: 8, marginTop: 8 }}>
                  {screenshots.slice(0, 6).map((url, i) => (
                    <img key={i} src={url} alt={`screen${i}`} loading="lazy"
                      onClick={() => setZoomImg(url)}
                      style={{ width: 110, borderRadius: 8, cursor: 'zoom-in', boxShadow: '0 2px 8px rgba(0,0,0,0.12)', objectFit: 'cover' }}
                    />
                  ))}
                </Box>
              ) : (
                <Typography variant="caption" color="textSecondary" style={{ display: 'block', marginTop: 8 }}>No screenshots available</Typography>
              )}
            </Grid>
          </Grid>
        </DialogContent>
      </Dialog>

      {/* Image zoom dialog */}
      <Dialog open={!!zoomImg} onClose={() => setZoomImg(null)} maxWidth="lg" PaperProps={{ style: { background: 'transparent', boxShadow: 'none' } }}>
        <DialogContent style={{ padding: 0, display: 'flex', justifyContent: 'center', alignItems: 'center', overflow: 'auto', maxHeight: '90vh' }}>
          <img src={zoomImg} alt="Zoomed" onClick={() => setZoomImg(null)}
            style={{ maxWidth: '85vw', maxHeight: '85vh', objectFit: 'contain', borderRadius: 10, boxShadow: '0 8px 40px rgba(0,0,0,0.6)', cursor: 'zoom-out' }}
          />
        </DialogContent>
      </Dialog>
    </>
  );
}

// ---------------------------------------------------------------------------
// Main export — split-panel layout so results are always visible
// ---------------------------------------------------------------------------
export default function AppAnalyser3() {
  const [selectedBatch, setSelectedBatch] = useState(null);
  const [snack, setSnack]                 = useState('');
  const [formOpen, setFormOpen]           = useState(false);

  return (
    <Box p={1} style={{ height: 'calc(100vh - 140px)', display: 'flex', flexDirection: 'column' }}>

      {/* ── New Batch toggle ── */}
      <Box mb={1} display="flex" alignItems="center" style={{ gap: 10 }}>
        <Button
          variant={formOpen ? 'contained' : 'outlined'}
          color="primary"
          startIcon={<Cloud />}
          onClick={() => setFormOpen(v => !v)}
          style={{ borderRadius: 8, fontWeight: 700 }}
        >
          {formOpen ? 'Hide Form' : 'New Batch'}
        </Button>
        {formOpen && (
          <Typography variant="caption" color="textSecondary">
            Fill in the form below and click "Start Batch"
          </Typography>
        )}
      </Box>

      {/* ── Collapsible create form ── */}
      {formOpen && (
        <Box mb={2}>
          <BatchForm onCreated={() => {
            setFormOpen(false);
            setSelectedBatch(null);
            setSnack('Batch created! It is running in the background.');
          }} />
        </Box>
      )}

      {/* ── Two-column layout: list | results ── */}
      <Box style={{ flexGrow: 1, overflow: 'hidden', display: 'flex', gap: 12 }}>

        {/* Left — batch list (fixed width, scrollable) */}
        <Box style={{
          width: 380, flexShrink: 0,
          overflowY: 'auto',
          display: 'flex', flexDirection: 'column',
        }}>
          <BatchList onSelect={id => setSelectedBatch(id)} selectedId={selectedBatch} />
        </Box>

        {/* Right — results panel (fills remaining space, scrollable) */}
        <Box style={{ flexGrow: 1, overflowY: 'auto' }}>
          {selectedBatch
            ? <BatchAppsTable batchId={selectedBatch} />
            : (
              <Box display="flex" flexDirection="column" alignItems="center" justifyContent="center"
                style={{ height: '100%', color: '#b2bec3', textAlign: 'center', padding: 40 }}>
                <Typography variant="h6" style={{ color: '#b2bec3', fontWeight: 600, marginBottom: 8 }}>
                  Select a batch to see results
                </Typography>
                <Typography variant="body2" style={{ color: '#dfe6e9' }}>
                  Click "Open" on any batch from the list on the left
                </Typography>
              </Box>
            )
          }
        </Box>

      </Box>

      <Snackbar
        open={!!snack}
        autoHideDuration={4000}
        onClose={() => setSnack('')}
        message={snack}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      />
    </Box>
  );
}
